import window from './window';
import { PUBLIC_SIGNED_IN_APP_CONFIG } from './constants';
const DEFAULT_LOCAL_STORAGE_SIGNED_IN_APPS = [
    PUBLIC_SIGNED_IN_APP_CONFIG,
];
const LOCAL_STORAGE_KEY = 'scoir-signed-in';
/**
 *
 * @returns The app name (student, parent, counselor, admissions, guidance, etc...)
 */
const getCurrentApp = () => {
    return window.location.pathname.split('/')[1];
};
/**
 * This function returns a string of the current time code
 * It's used to determine which app you're most recently signed in to
 * @returns a string of the current time code
 */
const getTimeString = () => {
    const d = new Date();
    const t = d.getTime();
    const s = d.getSeconds();
    const m = d.getMilliseconds();
    const str = `${t + s + m}`;
    return str;
};
/**
 *
 * @param app The app name (student, parent, counselor, admissions, guidance, etc...)
 * @param signedInApps The current array of signed in apps as pulled out of local storage
 * @returns true if the given app is in the array of signed in apps, false otherwise
 */
const isAppSignedIn = (app, signedInApps) => {
    const found = signedInApps.find((a) => a.app === app && a.lastSignedIn !== '1');
    return !!found;
};
/**
 * @param app The app name (student, parent, counselor, admissions, guidance, etc...)
 * @param supportedRedirects An array of the equivalent pages this app supports (e.g. [COLLEGE_PROFILE])
 * @param signedInApps The current array of signed in apps as pulled out of local storage
 * @returns A new array of signed in apps with the given app added
 */
const addSignedInApp = (app, supportedRedirects = [], signedInApps) => {
    const newSignedInApp = {
        app,
        lastSignedIn: getTimeString(),
        supportedRedirects: {},
    };
    supportedRedirects.forEach((r) => {
        newSignedInApp.supportedRedirects[r] = true;
    });
    signedInApps.unshift(newSignedInApp);
    return signedInApps;
};
/**
 * @param app The app name (student, parent, counselor, admissions, guidance, etc...)
 * @param signedInApps The current array of signed in apps as pulled out of local storage
 * @returns A new array of signed in apps with the given app removed
 */
const removeSignedInApp = (app, signedInApps) => {
    return signedInApps.filter((a) => a.app !== app || a.lastSignedIn === PUBLIC_SIGNED_IN_APP_CONFIG.lastSignedIn);
};
/**
 * Call getSignedInApps to get the array of apps you're currently signed in to from local storage
 */
const getSignedInApps = () => {
    const signedInApps = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!signedInApps || signedInApps === '[]') {
        return DEFAULT_LOCAL_STORAGE_SIGNED_IN_APPS;
    }
    return JSON.parse(signedInApps);
};
/**
 * Call clearApp when signing out of an app, to update local storage
 */
export const clearSignedInApp = (app) => {
    const currentSignedInApps = getSignedInApps();
    const updated = removeSignedInApp(app, currentSignedInApps);
    const str = JSON.stringify(updated);
    window.localStorage.setItem(LOCAL_STORAGE_KEY, str);
};
/**
 * Call storeSignedInApp when signing in to an app, to update local storage
 * @param app The app name (student, parent, counselor, admissions, guidance, etc...)
 * @param supportedRedirects An array of the equivalent pages this app supports (e.g. [COLLEGE_PROFILE])
 * @returns void
 */
export const setSignedInApp = (app, ...supportedRedirects) => {
    const currentSignedInApps = getSignedInApps();
    if (isAppSignedIn(app, currentSignedInApps))
        return;
    const updated = addSignedInApp(app, supportedRedirects, currentSignedInApps);
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
};
/**
 * Call getAvailableApps to get the array of apps you're currently signed in to from local storage that support the given equivalent page
 * @param equivalentPageName The equivalent page name (e.g. 'college-profile')
 * @returns An array of app names (student, parent, counselor, admissions, guidance, etc...)
*/
const getAppsThatSupportEquivalentPage = (equivalentPageName) => {
    const signedInApps = getSignedInApps();
    const supportedApps = signedInApps.filter((a) => {
        return a.supportedRedirects[equivalentPageName];
    });
    return supportedApps;
};
/**
 * This function returns the app you're most recently signed in to
 * @param supportedApps An array of apps you're currently signed in to that support the given equivalent page
 * @returns The app you're most recently signed in to
 */
const getLatestSignedInApp = (supportedApps = []) => {
    const latestSignIn = (a, b) => {
        return a.lastSignedIn > b.lastSignedIn ? -1 : 1;
    };
    return supportedApps.sort(latestSignIn)[0];
};
/**
 * This function returns the app you're most recently signed in to that supports the given equivalent page
 * @param equivalentPageName The equivalent page name (e.g. 'college-profile')
 * @returns The app you're most recently signed in to that supports the given equivalent page preferring the current app you're in if its an option
 */
export const getBestAppToRedirectTo = (equivalentPageName) => {
    var _a;
    const currentApp = getCurrentApp();
    const supportedApps = getAppsThatSupportEquivalentPage(equivalentPageName);
    const currentAppIsSupported = supportedApps.find((a) => {
        if (supportedApps.length > 1) {
            // if there's more than one supported app, prefer the signed in app over public app
            return a.app === currentApp && a.lastSignedIn !== '1';
        }
        return a.app === currentApp;
    });
    if (supportedApps.length === 0 || currentAppIsSupported)
        return null;
    return (_a = getLatestSignedInApp(supportedApps)) === null || _a === void 0 ? void 0 : _a.app;
};
